import { useEffect, useState } from 'react'
import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
  doc,
} from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { UserProfile } from '@/types'
import {
  normalizeStepsData,
  normalizeSleepData,
  normalizeHeartRateData,
  normalizeSpO2Data,
  normalizeStressData,
  normalizeBloodPressureData,
  normalizeWeightData,
  normalizeWorkoutData,
} from '@/lib/dataValidation'

// ============================================
// User Detail Hook
// ============================================

export function useUserDetail(uid: string) {
  const [user, setUser] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!uid) {
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    const unsub = onSnapshot(
      doc(db, 'users', uid),
      (snapshot) => {
        if (snapshot.exists()) {
          setUser({ uid: snapshot.id, ...snapshot.data() } as UserProfile)
        } else {
          setUser(null)
        }
        setLoading(false)
      },
      (err) => {
        console.error('Error fetching user:', err)
        setError(err)
        setLoading(false)
      }
    )

    return () => unsub()
  }, [uid])

  return { user, loading, error }
}

// ============================================
// Subcollection Hook with Fallback
// ============================================

export function useUserSubcollection<T>(uid: string, sub: string, limitCount = 50) {
  const [data, setData] = useState<T[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!uid) {
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    const colRef = collection(db, 'users', uid, sub)
    
    const unsub = onSnapshot(
      colRef,
      (snapshot) => {
        try {
          const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as T))
          setData(items)
          setLoading(false)
        } catch (err) {
          console.error(`Error processing ${sub}:`, err)
          setError(err instanceof Error ? err : new Error('Processing error'))
          setLoading(false)
        }
      },
      (err) => {
        console.error(`Error fetching ${sub}:`, err)
        setError(err)
        setLoading(false)
      }
    )

    return () => unsub()
  }, [uid, sub, limitCount])

  return { data, loading, error }
}

// ============================================
// Heart Rate Hook with Validation
// ============================================

export function useUserHeartRate(uid: string) {
  const [data, setData] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!uid) {
      setLoading(false)
      return
    }

    const q = query(
      collection(db, 'users', uid, 'heartRateHistory'),
      orderBy('timestamp', 'desc'),
      limit(100)
    )

    const unsub = onSnapshot(
      q,
      (s) => {
        try {
          const items = s.docs.map(d => normalizeHeartRateData({ id: d.id, ...d.data() }))
          setData(items)
          setLoading(false)
        } catch (err) {
          console.error('Error processing heart rate:', err)
          setError(err instanceof Error ? err : new Error('Processing error'))
          setLoading(false)
        }
      },
      (err) => {
        console.error('Error fetching heart rate:', err)
        setError(err)
        setLoading(false)
      }
    )

    return () => unsub()
  }, [uid])

  return { data, loading, error }
}

// ============================================
// Steps Hook - FIXED: Read from health collection
// Mobile app stores steps in health/{date} not stepsHistory
// ============================================

export function useUserSteps(uid: string) {
  const [data, setData] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!uid) {
      setLoading(false)
      return
    }

    // Mobile app stores daily steps in 'health' collection
    const q = query(
      collection(db, 'users', uid, 'health'),
      orderBy('date', 'desc'),
      limit(30)
    )

    const unsub = onSnapshot(
      q,
      (s) => {
        try {
          const items = s.docs
            .map(d => normalizeStepsData({ id: d.id, ...d.data() }))
            .filter(d => d.steps > 0)
            .sort((a, b) => b.date.localeCompare(a.date)) // Sort by date desc

          setData(items)
          setLoading(false)
        } catch (err) {
          console.error('Error processing steps:', err)
          setError(err instanceof Error ? err : new Error('Processing error'))
          setLoading(false)
        }
      },
      (err) => {
        console.error('Error fetching steps:', err)
        setError(err)
        setLoading(false)
      }
    )

    return () => unsub()
  }, [uid])

  return { data, loading, error }
}

// ============================================
// Sleep Hook with Validation
// ============================================

export function useUserSleep(uid: string) {
  const [data, setData] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!uid) {
      setLoading(false)
      return
    }

    const q = query(
      collection(db, 'users', uid, 'sleepHistory'),
      orderBy('date', 'desc'),
      limit(30)
    )

    const unsub = onSnapshot(
      q,
      (s) => {
        try {
          const items = s.docs.map(d => normalizeSleepData({ id: d.id, ...d.data() }))
          setData(items)
          setLoading(false)
        } catch (err) {
          console.error('Error processing sleep:', err)
          setError(err instanceof Error ? err : new Error('Processing error'))
          setLoading(false)
        }
      },
      (err) => {
        console.error('Error fetching sleep:', err)
        setError(err)
        setLoading(false)
      }
    )

    return () => unsub()
  }, [uid])

  return { data, loading, error }
}

// ============================================
// Workout Hook with Validation
// ============================================

export function useUserWorkouts(uid: string) {
  const [data, setData] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!uid) {
      setLoading(false)
      return
    }

    const q = query(
      collection(db, 'users', uid, 'workoutHistory'),
      orderBy('startTime', 'desc'),
      limit(20)
    )

    const unsub = onSnapshot(
      q,
      (s) => {
        try {
          const items = s.docs.map(d => normalizeWorkoutData({ id: d.id, ...d.data() }))
          setData(items)
          setLoading(false)
        } catch (err) {
          console.error('Error processing workouts:', err)
          setError(err instanceof Error ? err : new Error('Processing error'))
          setLoading(false)
        }
      },
      (err) => {
        console.error('Error fetching workouts:', err)
        setError(err)
        setLoading(false)
      }
    )

    return () => unsub()
  }, [uid])

  return { data, loading, error }
}

// ============================================
// Health Summary Hook with Validation
// ============================================

export function useUserHealthSummary(uid: string) {
  const [data, setData] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!uid) {
      setLoading(false)
      return
    }

    const q = query(
      collection(db, 'users', uid, 'health'),
      orderBy('date', 'desc'),
      limit(30)
    )

    const unsub = onSnapshot(
      q,
      (s) => {
        try {
          const items = s.docs.map(d => {
            const raw = d.data()
            const normalized = normalizeStepsData({ id: d.id, ...raw })
            return {
              ...normalized,
              avgHr: raw.heartRateAvg || raw.heartRate || 0,
              hrMin: raw.heartRateMin || 0,
              hrMax: raw.heartRateMax || 0,
              sleep: raw.sleepTotal || 0,
              stress: raw.stress || null,
              bpSystolic: raw.bpSystolic || null,
              bpDiastolic: raw.bpDiastolic || null,
              updatedAt: raw.updatedAt,
            }
          })
          setData(items)
          setLoading(false)
        } catch (err) {
          console.error('Error processing health summary:', err)
          setError(err instanceof Error ? err : new Error('Processing error'))
          setLoading(false)
        }
      },
      (err) => {
        console.error('Error fetching health summary:', err)
        setError(err)
        setLoading(false)
      }
    )

    return () => unsub()
  }, [uid])

  return { data, loading, error }
}

// ============================================
// Sensor Hooks with Validation
// ============================================

export function useUserSpO2(uid: string, limitCount = 100) {
  const [data, setData] = useState<any[]>([])
  const [stats, setStats] = useState<{ avg: number; min: number; max: number; latest: number | null }>({
    avg: 0, min: 0, max: 0, latest: null,
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!uid) return

    const q = query(
      collection(db, 'users', uid, 'spo2History'),
      orderBy('timestamp', 'desc'),
      limit(limitCount)
    )

    const unsub = onSnapshot(q, (snapshot) => {
      try {
        const items = snapshot.docs.map(d => normalizeSpO2Data({ id: d.id, ...d.data() }))
        setData(items)

        if (items.length > 0) {
          const values = items.map(i => i.value).filter(v => v > 0)
          if (values.length > 0) {
            setStats({
              avg: Math.round(values.reduce((a, b) => a + b, 0) / values.length),
              min: Math.min(...values),
              max: Math.max(...values),
              latest: values[0],
            })
          }
        }
        setLoading(false)
        setError(null)
      } catch (err) {
        console.error('Error processing SpO2:', err)
        setError(err instanceof Error ? err : new Error('Processing error'))
        setLoading(false)
      }
    }, (err) => {
      console.error('Error fetching SpO2:', err)
      setError(err)
      setLoading(false)
    })

    return () => unsub()
  }, [uid, limitCount])

  return { data, stats, loading, error }
}

export function useUserStress(uid: string, limitCount = 100) {
  const [data, setData] = useState<any[]>([])
  const [stats, setStats] = useState<{ avg: number; latest: number | null; level: 'low' | 'moderate' | 'high' | null }>({
    avg: 0, latest: null, level: null,
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!uid) return

    const q = query(
      collection(db, 'users', uid, 'stressHistory'),
      orderBy('timestamp', 'desc'),
      limit(limitCount)
    )

    const unsub = onSnapshot(q, (snapshot) => {
      try {
        const items = snapshot.docs.map(d => normalizeStressData({ id: d.id, ...d.data() }))
        setData(items)

        if (items.length > 0) {
          const values = items.map(i => i.level).filter(v => v > 0)
          if (values.length > 0) {
            const avg = Math.round(values.reduce((a, b) => a + b, 0) / values.length)
            const latest = values[0]
            let level: 'low' | 'moderate' | 'high' | null = null
            if (latest <= 30) level = 'low'
            else if (latest <= 60) level = 'moderate'
            else level = 'high'
            setStats({ avg, latest, level })
          }
        }
        setLoading(false)
        setError(null)
      } catch (err) {
        console.error('Error processing stress:', err)
        setError(err instanceof Error ? err : new Error('Processing error'))
        setLoading(false)
      }
    }, (err) => {
      console.error('Error fetching stress:', err)
      setError(err)
      setLoading(false)
    })

    return () => unsub()
  }, [uid, limitCount])

  return { data, stats, loading, error }
}

export function useUserBloodPressure(uid: string, limitCount = 100) {
  const [data, setData] = useState<any[]>([])
  const [stats, setStats] = useState<{
    systolic: { avg: number; latest: number | null }
    diastolic: { avg: number; latest: number | null }
    classification: string | null
  }>({
    systolic: { avg: 0, latest: null },
    diastolic: { avg: 0, latest: null },
    classification: null,
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!uid) return

    const q = query(
      collection(db, 'users', uid, 'bpHistory'),
      orderBy('timestamp', 'desc'),
      limit(limitCount)
    )

    const unsub = onSnapshot(q, (snapshot) => {
      try {
        const items = snapshot.docs.map(d => normalizeBloodPressureData({ id: d.id, ...d.data() }))
        setData(items)

        if (items.length > 0) {
          const systolics = items.map(i => i.systolic).filter(v => v > 0)
          const diastolics = items.map(i => i.diastolic).filter(v => v > 0)

          if (systolics.length > 0 && diastolics.length > 0) {
            const sysAvg = Math.round(systolics.reduce((a, b) => a + b, 0) / systolics.length)
            const diaAvg = Math.round(diastolics.reduce((a, b) => a + b, 0) / diastolics.length)
            
            setStats({
              systolic: { avg: sysAvg, latest: systolics[0] },
              diastolic: { avg: diaAvg, latest: diastolics[0] },
              classification: items[0]?.classification || null,
            })
          }
        }
        setLoading(false)
        setError(null)
      } catch (err) {
        console.error('Error processing BP:', err)
        setError(err instanceof Error ? err : new Error('Processing error'))
        setLoading(false)
      }
    }, (err) => {
      console.error('Error fetching BP:', err)
      setError(err)
      setLoading(false)
    })

    return () => unsub()
  }, [uid, limitCount])

  return { data, stats, loading, error }
}

export function useUserWeight(uid: string, limitCount = 30) {
  const [data, setData] = useState<any[]>([])
  const [stats, setStats] = useState<{
    latest: number | null
    min: number | null
    max: number | null
    change: number | null
  }>({ latest: null, min: null, max: null, change: null })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!uid) return

    const q = query(
      collection(db, 'users', uid, 'weightHistory'),
      orderBy('timestamp', 'desc'),
      limit(limitCount)
    )

    const unsub = onSnapshot(q, (snapshot) => {
      try {
        const items = snapshot.docs.map(d => normalizeWeightData({ id: d.id, ...d.data() }))
        setData(items)

        if (items.length > 0) {
          const weights = items.map(i => i.weightKg).filter(v => v > 0)
          if (weights.length > 0) {
            const latest = weights[0]
            const min = Math.min(...weights)
            const max = Math.max(...weights)
            const change = weights.length > 1 ? latest - weights[weights.length - 1] : 0

            setStats({ latest, min, max, change })
          }
        }
        setLoading(false)
        setError(null)
      } catch (err) {
        console.error('Error processing weight:', err)
        setError(err instanceof Error ? err : new Error('Processing error'))
        setLoading(false)
      }
    }, (err) => {
      console.error('Error fetching weight:', err)
      setError(err)
      setLoading(false)
    })

    return () => unsub()
  }, [uid, limitCount])

  return { data, stats, loading, error }
}

// ============================================
// Combined Hook for All Health Data
// ============================================

export function useUserHealthData(uid: string) {
  const heartRate = useUserHeartRate(uid)
  const steps = useUserSteps(uid)
  const sleep = useUserSleep(uid)
  const workouts = useUserWorkouts(uid)
  const healthSummary = useUserHealthSummary(uid)
  const spo2 = useUserSpO2(uid)
  const stress = useUserStress(uid)
  const bloodPressure = useUserBloodPressure(uid)
  const weight = useUserWeight(uid)

  const isLoading =
    heartRate.loading ||
    steps.loading ||
    sleep.loading ||
    workouts.loading ||
    healthSummary.loading ||
    spo2.loading ||
    stress.loading ||
    bloodPressure.loading ||
    weight.loading

  const hasError =
    heartRate.error ||
    steps.error ||
    sleep.error ||
    workouts.error ||
    healthSummary.error ||
    spo2.error ||
    stress.error ||
    bloodPressure.error ||
    weight.error

  return {
    heartRate,
    steps,
    sleep,
    workouts,
    healthSummary,
    spo2,
    stress,
    bloodPressure,
    weight,
    isLoading,
    hasError,
  }
}
